import { ImageProps } from 'next/image'
import {
  TcClickTypes,
  TcPageTemplateNames,
} from 'src/configuration/tagcommander'
import { getDayNumber } from 'src/utils/TimeUtils'

import routes from '../../routes/definitions'
import { Day, Moment, Recipe, RecipeCategory } from '../../types/fam-graphql'
import { ContentCardProps } from '../../components/ContentCard'
import ActionButton from '../../components/ActionButton'
import { colors } from '../../theme'
import Router from '../../routes/router'
import tcClick from '../tcClick/tcClick'
import { api, app } from '../../configuration'

export const formatDuration = (t: any, duration: number) => {
  if (duration > 0) {
    if (duration < 60) {
      return t('recipe_duration_minutes', { minutes: duration })
    }
    return t('recipe_duration_hours', {
      hours: Math.floor(duration / 60),
      minutes: `${duration % 60}`.padStart(2, '0'),
    })
  }
  return null
}

export const hasSteps = (steps: Recipe['steps']) => {
  return steps && steps?.length > 0 && !steps?.[0]?.text?.includes(`null`)
}

export const recipeCardColorTransformer = (
  recipeCategory: Recipe['recipe_category']
) => {
  switch (recipeCategory) {
    case 'COURSE':
    case 'FULL_COURSE':
    case 'SIDE':
    case 'OTHER':
      return colors.redMandy
    case 'DESSERT':
      return colors.redMojo
    case 'START':
      return colors.cornflowerBlue
    default:
      return ''
  }
}

export const recipeCardTransformer = (
  t: any,
  recipe: Recipe,
  day?: Day,
  moment?: Moment,
  onSwitchCourse?: (id: string) => void,
  unchangeable?: boolean,
  fromPlanning?: boolean,
  earlierPlanningId?: string,
  isFromRecipeList?: boolean,
  tcVars?: {
    chapter2: string
    chapter3: string
  },
  isFromSwitchCourse?: boolean,
  isOffline?: boolean,
  parentRecipeSlug?: string
): ContentCardProps => {
  const hasLink = hasSteps(recipe.steps) || recipe?.parent
  const singleButton =
    hasLink && !(!unchangeable && ((day && moment) || onSwitchCourse))

  const firstButtonActionProps = {
    ...(fromPlanning || isFromRecipeList
      ? {
          onClickHandler: () =>
            tcClick({
              click_name: fromPlanning
                ? `${t('recipe_see_recipe_cta')}_${moment}_${recipe.name}_${
                    TcPageTemplateNames.Another
                  }_fam`
                : `${t('recipe_see_recipe_cta')}_crosslinking_${
                    TcPageTemplateNames.Recipes
                  }_fam`,
              click_type: TcClickTypes.navigation,
              chapter1: 'navigation fam',
              chapter2: tcVars?.chapter2,
              chapter3: tcVars?.chapter3,
            }),
        }
      : {}),
    ...(earlierPlanningId
      ? Router.getRouteConfig(routes.recipe.name, {
          earlierPlanningId,
          slug: recipe?.slug ?? '',
        })
      : fromPlanning
      ? Router.getRouteConfig(routes.recipe.name, {
          slug: recipe?.slug ?? '',
          jour: day && `jour_${getDayNumber(day)}`,
          moment,
          fromPlanning,
        })
      : Router.getRouteConfig(routes.recipe.name, {
          slug: recipe?.slug ?? '',
          ...(parentRecipeSlug && {
            parent: parentRecipeSlug,
          }),
        })),
  }

  return {
    id: recipe?.id,
    backgroundColor: recipeCardColorTransformer(recipe?.recipe_category ?? ''),
    imageProps: {
      src:
        recipe?.image ?? app.ASSET_PREFIX + '/images/default/placeholder.png', // add prefix because of unoptimized
      alt: recipe?.image_alt ?? recipe?.name ?? '',
      objectFit: 'cover',
      layout: 'fill',
      unoptimized: isOffline,
      fallbacksrc:
        api.API_URL +
        `/storage/recettes_generiques/${t(
          `recipe_type_${recipe.recipe_category}_img_fallback`
        )}`,
    } as ImageProps,
    singleButtonActionProps:
      isFromSwitchCourse || !hasLink ? undefined : firstButtonActionProps,
    firstHighlightText:
      recipe?.recipe_category !== RecipeCategory.Dessert
        ? t(`recipe_type_${recipe?.recipe_category ?? ''}`)
        : t(`recipe_type_${recipe?.ranking ?? ''}`),
    secondHighlightText: recipe?.name ?? '',
    duration: formatDuration(
      t,
      (recipe?.baking_time ?? 0) + (recipe?.preparation_time ?? 0)
    ),
    buttons: isFromSwitchCourse ? (
      <ActionButton
        className="-no-parent-hover-effect"
        text={t(`recipe_switch_type_${recipe?.recipe_category}_cta`)}
        variantType="link"
        variantColorScheme={'colored'}
        variantColor={colors.redMojo}
        disabled={isOffline}
        actionProps={{
          onClickHandler: (e) => {
            e?.preventDefault?.()
            onSwitchCourse?.(recipe?.id)
          },
        }}
      />
    ) : !hasLink ? undefined : (
      <>
        <ActionButton
          text={
            recipe?.parent
              ? t('recipe_see_recipes_cta')
              : t('recipe_see_recipe_cta')
          }
          variantType={singleButton ? 'fake' : 'fake'}
          actionProps={firstButtonActionProps}
        />
        {(!singleButton || !hasLink) && (
          <ActionButton
            className="-no-parent-hover-effect"
            text={t('recipe_switch_recipe_cta')}
            variantType="link"
            variantColorScheme={'colored'}
            variantColor={colors.redMojo}
            disabled={isOffline}
            actionProps={{
              onClickHandler: onSwitchCourse
                ? (e) => {
                    e?.preventDefault?.()
                    onSwitchCourse(recipe?.id)
                  }
                : fromPlanning
                ? () => {
                    tcClick({
                      click_name: `${t('recipe_switch_recipe_cta')}_${moment}_${
                        recipe.name
                      }_${TcPageTemplateNames.Another}_fam`,
                      click_type: TcClickTypes.navigation,
                      chapter1: 'navigation fam',
                      chapter2: t('breadcrumb_eat_better'),
                      chapter3: t('breadcrumb_fam'),
                    })
                    tcClick({
                      click_name: 'click',
                      click_type: TcClickTypes.action,
                      chapter1: 'changement_menu',
                    }) // seems duplicate but asked in the new tracking plan
                  }
                : undefined,
              ...(day &&
                moment &&
                Router.getRouteConfig(routes.switchcourse.name, {
                  dayNumber: `jour_${getDayNumber(day)}`,
                  moment,
                  id: recipe?.id ?? '',
                })),
            }}
          />
        )}
      </>
    ),
  }
}

export const combinedRecipeCardTransformer = (
  t: any,
  recipe: Recipe,
  side: Recipe,
  day?: Day,
  moment?: Moment,
  onSwitchCourse?: (id: string) => void,
  unchangeable?: boolean,
  fromPlanning?: boolean,
  earlierPlanningId?: string,
  isOffline?: boolean
): ContentCardProps => {
  const singleButton = !(!unchangeable && ((day && moment) || onSwitchCourse))

  const firstButtonActionProps = {
    ...(earlierPlanningId
      ? Router.getRouteConfig(routes.recipe.name, {
          earlierPlanningId,
          slug: recipe?.slug ?? '',
        })
      : fromPlanning
      ? Router.getRouteConfig(routes.recipe.name, {
          slug: recipe?.slug ?? '',
          side: side?.slug ?? '',
          jour: day && `jour_${getDayNumber(day)}`,
          moment,
          earlierPlanningId,
          fromPlanning,
        })
      : Router.getRouteConfig(routes.recipe.name, {
          earlierPlanningId,
          slug: recipe?.slug ?? '',
          side: side?.slug ?? '',
        })),
  }

  return {
    id: recipe?.id,
    backgroundColor: recipeCardColorTransformer(RecipeCategory.FullCourse),
    imageProps: {
      src:
        recipe?.image ?? app.ASSET_PREFIX + '/images/default/placeholder.png', // add prefix because of unoptimized
      alt: recipe?.image_alt ?? recipe?.name ?? '',
      objectFit: 'cover',
      layout: 'fill',
      unoptimized: isOffline,
    } as ImageProps,
    firstHighlightText: t(`recipe_combined_label`),
    secondHighlightText: t('recipe_combined_title', {
      course: recipe?.name ?? '',
      side: side?.name ?? '',
    }),
    duration: formatDuration(
      t,
      (recipe?.baking_time ?? 0) +
        (recipe?.preparation_time ?? 0) +
        (side?.baking_time ?? 0) +
        (side?.preparation_time ?? 0)
    ),
    singleButtonActionProps: firstButtonActionProps,
    buttons: (
      <>
        <ActionButton
          text={t('recipe_see_recipe_cta')}
          variantType={'fake'}
          actionProps={firstButtonActionProps}
        />
        {!singleButton && (
          <ActionButton
            className="-no-parent-hover-effect"
            text={t('recipe_switch_recipe_cta')}
            variantType="link"
            variantColorScheme={'colored'}
            variantColor={colors.redMojo}
            disabled={isOffline}
            actionProps={{
              onClickHandler: onSwitchCourse
                ? (e) => {
                    e?.preventDefault?.()
                    onSwitchCourse(recipe?.id)
                  }
                : undefined,
              ...(day &&
                moment &&
                Router.getRouteConfig(routes.switchcourse.name, {
                  dayNumber: `jour_${getDayNumber(day)}`,
                  moment,
                  id: recipe?.id ?? '',
                  side: side?.id ?? '',
                })),
            }}
          />
        )}
      </>
    ),
  }
}

function sortDessertOnLastPosition(recipeArr: Recipe[]) {
  const desserts: Recipe[] = []

  const newList = recipeArr.filter((r) => {
    if (r.recipe_category === RecipeCategory.Dessert) {
      desserts.push(r)
      return false
    }
    return true
  })

  return [...newList, ...desserts]
}

function putStarterOnFirstPosition(recipeArr: ContentCardProps[], t: any) {
  const starters: ContentCardProps[] = []

  const newList = recipeArr.filter((r) => {
    if (r.firstHighlightText === t('recipe_type_START')) {
      starters.push(r)
      return false
    }
    return true
  })

  return [...starters, ...newList]
}

export const mealTransformer = (args: {
  t: any
  recipes?: Recipe[] | null
  day?: Day
  moment?: Moment
  onSwitchCourse?: (id: string) => void
  unchangeable?: boolean
  fromPlanning?: boolean
  earlierPlanningId?: string
  isOffline?: boolean
}): ContentCardProps[] | undefined => {
  const {
    t,
    recipes,
    day,
    moment,
    onSwitchCourse,
    unchangeable,
    fromPlanning,
    earlierPlanningId,
    isOffline,
  } = args
  const parsedRecipes = recipes?.length
    ? sortDessertOnLastPosition(recipes)
    : recipes
  const side = parsedRecipes?.find(
    (r) => r.recipe_category === RecipeCategory.Side
  )
  const course = parsedRecipes?.find(
    (r) => r.recipe_category === RecipeCategory.Course
  )

  if (side && course) {
    let firstDessertIndex: null | number = null
    const cards = parsedRecipes
      ?.filter(
        (r) =>
          ![RecipeCategory.Side, RecipeCategory.Course].includes(
            r.recipe_category
          )
      )
      ?.map((recipe, idx) => {
        if (
          recipe.recipe_category === RecipeCategory.Dessert &&
          firstDessertIndex === null
        ) {
          firstDessertIndex = idx
        }
        return recipeCardTransformer(
          t,
          recipe,
          day,
          moment,
          onSwitchCourse,
          unchangeable,
          fromPlanning,
          undefined,
          undefined,
          undefined,
          undefined,
          isOffline
        )
      })
    if (firstDessertIndex) {
      cards?.splice(
        firstDessertIndex,
        0,
        combinedRecipeCardTransformer(
          t,
          course,
          side,
          day,
          moment,
          onSwitchCourse,
          unchangeable,
          fromPlanning,
          undefined,
          isOffline
        )
      )
    } else {
      cards?.unshift(
        combinedRecipeCardTransformer(
          t,
          course,
          side,
          day,
          moment,
          onSwitchCourse,
          unchangeable,
          fromPlanning,
          undefined,
          isOffline
        )
      )
    }

    return putStarterOnFirstPosition(cards ?? [], t)
  }

  return parsedRecipes?.map((recipe) =>
    recipeCardTransformer(
      t,
      recipe,
      day,
      moment,
      onSwitchCourse,
      unchangeable,
      fromPlanning,
      earlierPlanningId,
      undefined,
      undefined,
      undefined,
      isOffline
    )
  )
}
